import { Fragment } from 'preact'
import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from 'preact/hooks'
import withStyles from './jss'
import config from '../config'
import { memo } from 'preact/compat'

const height = 400
const stroke = 2
const axis = 20
const axisGap = 20
const day = 24 * 60 * 60 * 1000

const strokeHoverWidth = 12

const timeToX = ({ minX, maxX, time, width }) => {
  return ((time - minX) / (maxX - minX)) * width
}

const uuidToColor = uuid => {
  const uuidInt = parseInt(uuid.slice(-12), 16) + 1
  // Random primes
  const g = (uuidInt * 89) % 128
  const b = (uuidInt * 53) % 128
  return `rgba(186, ${g}, ${b}, 0.8)`
}

const pointsToPolyline = ({
  id,
  name,
  currentScore,
  points,
  maxX,
  minX,
  maxY,
  width,
}) => {
  const commands = points.map(point => {
    return `${timeToX({ minX, maxX, time: point.time, width })} ${
      (1 - point.score / maxY) * height
    }`
  })
  return {
    color: uuidToColor(id),
    name,
    currentScore,
    points: commands.join(','),
  }
}

const getXLabels = ({ minX, maxX, width }) => {
  const labels = []
  const step = Math.ceil((((maxX - minX) / width) * 200) / day) * day
  let labelStart = new Date(minX).setHours(0, 0, 0, 0)
  if (labelStart % step !== 0) {
    labelStart += step
  }

  for (let label = labelStart; label <= maxX; label += step) {
    labels.push({
      label: new Date(label).toLocaleDateString(),
      x: timeToX({ minX, maxX, time: label, width }),
    })
  }
  return labels
}

const GraphLine = memo(
  ({
    points,
    onTooltipIn,
    onMouseMove,
    onMouseOut,
    name,
    currentScore,
    ...rest
  }) => (
    <Fragment>
      <polyline
        {...rest}
        points={points}
        strokeWidth={stroke}
        strokeLinecap='round'
        fill='transparent'
        pointerEvents='none'
      />
      <polyline
        strokeWidth={strokeHoverWidth}
        points={points}
        strokeLinecap='round'
        fill='transparent'
        pointerEvents='stroke'
        onMouseOver={onTooltipIn(`${name} - ${currentScore} points`)}
        onMouseMove={onMouseMove}
        onMouseOut={onMouseOut}
      />
    </Fragment>
  )
)

function Graph({ graphData, classes }) {
  const svgRef = useRef(null)
  const [width, setWidth] = useState(window.innerWidth)
  const updateWidth = useCallback(() => {
    if (svgRef.current === null) return
    setWidth(svgRef.current.getBoundingClientRect().width)
  }, [])

  const [tooltipData, setTooltipData] = useState({
    x: 0,
    y: 0,
    content: '',
  })

  useLayoutEffect(() => {
    updateWidth()
  }, [updateWidth])
  useEffect(() => {
    function handleResize() {
      updateWidth()
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [updateWidth])

  const { polylines, labels } = useMemo(() => {
    if (!graphData || graphData.length === 0) {
      return {
        polylines: [],
        labels: [],
      }
    }
    const minX = config.startTime
    const maxX = Math.min(Date.now(), config.endTime)
    let maxY = 0
    graphData.graph.forEach(user => {
      user.points.forEach(point => {
        if (point.score > maxY) {
          maxY = point.score
        }
      })
    })
    const labels = getXLabels({ minX, maxX, width })
    const polylines = graphData.graph.map(user =>
      pointsToPolyline({
        points: user.points,
        id: user.id,
        name: user.name,
        currentScore: user.points[0].score,
        maxX,
        minX,
        maxY,
        width,
      })
    )
    return { polylines, labels }
  }, [graphData, width])

  const handleTooltipIn = useCallback(
    content => () => {
      setTooltipData(d => ({
        ...d,
        content,
      }))
    },
    []
  )

  const handleTooltipMove = useCallback(evt => {
    setTooltipData(d => ({
      ...d,
      x: evt.clientX,
      y: evt.clientY,
    }))
  }, [])

  const handleTooltipOut = useCallback(() => {
    setTooltipData(d => ({
      ...d,
      content: '',
    }))
  }, [])

  if (graphData === null) {
    return null
  }

  return (
    <div className={`frame ${classes.root}`}>
      <div className='frame__body'>
        <svg
          ref={svgRef}
          viewBox={`${-stroke - axis} ${-stroke} ${width + stroke * 2 + axis} ${
            height + stroke * 2 + axis + axisGap
          }`}
        >
          <Fragment>
            {polylines.map(({ points, color, name, currentScore }, i) => (
              <GraphLine
                key={i}
                stroke={color}
                points={points}
                name={name}
                currentScore={currentScore}
                onMouseMove={handleTooltipMove}
                onMouseOut={handleTooltipOut}
                onTooltipIn={handleTooltipIn}
              />
            ))}
          </Fragment>
          <Fragment>
            {labels.map((label, i) => (
              <text x={label.x} y={height + axis + axisGap} key={i} fill='#fff'>
                {label.label}
              </text>
            ))}
          </Fragment>
          <line
            x1={-axisGap}
            y1={height + axisGap}
            x2={width}
            y2={height + axisGap}
            stroke='var(--cirrus-bg)'
            strokeLinecap='round'
            strokeWidth={stroke}
          />
          <line
            x1={-axisGap}
            y1='0'
            x2={-axisGap}
            y2={height + axisGap}
            stroke='var(--cirrus-bg)'
            strokeLinecap='round'
            strokeWidth={stroke}
          />
        </svg>
      </div>
      {tooltipData.content && (
        <div
          className={classes.tooltip}
          style={{
            transform: `translate(${tooltipData.x}px, ${tooltipData.y}px)`,
          }}
        >
          {tooltipData.content}
        </div>
      )}
    </div>
  )
}

export default withStyles(
  {
    root: {
      marginBottom: '20px',
      background: '#222',
      '& .frame__body': {
        padding: '20px',
      },
    },
    tooltip: {
      position: 'fixed',
      pointerEvents: 'none',
      background: '#fff',
      color: '#222',
      padding: '5px 10px',
      borderRadius: '5px',
      margin: '5px',
      top: '0',
      left: '0',
    },
  },
  Graph
)
